import React, { useState, useEffect, useRef } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const HOOSQuestionnaire = ({ patientData, taskId, language = 'de', onComplete, onClose }) => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);
  const t = translations[language] || translations.en;

  // Fonction utilitaire pour créer un timestamp local sans décalage UTC
  const createLocalTimestamp = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          setResponses(existingResponses);
          setIsEditing(true);
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  // Gérer le scroll intelligent des sections
  useEffect(() => {
    const handleScroll = () => {
      const container = document.querySelector('.questionnaire-content');
      if (!container) return;

      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      // Trouver la section active basée sur la position de scroll
      let newActiveSection = 0;
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const relativeTop = rect.top - containerRect.top;

          if (relativeTop <= 100) { // Marge de 100px
            newActiveSection = index;
          }
        }
      });

      setActiveSection(newActiveSection);
    };

    const container = document.querySelector('.questionnaire-content');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Questions du questionnaire HOOS en allemand
  const questions = {
    de: [
      {
        id: 'symptoms',
        title: 'Subskala Symptome',
        instruction: 'Die folgenden Fragen betreffen die Steifigkeit im Hüftgelenk, die Sie in der letzten Woche wahrgenommen haben. Steifigkeit ist ein Gefühl von Einschränkung oder Langsamkeit in den Bewegungen ("harzige Bewegungen") des Hüftgelenks.',
        questions: [
          {
            id: 'HOOS_SYM1',
            question: 'Spüren Sie ein Reiben, hören Sie ein Klicken, Knirschen, Knacken oder ein anderes Geräusch in der Hüfte?',
            options: [
              { value: 0, text: 'nie' },
              { value: 1, text: 'selten' },
              { value: 2, text: 'manchmal' },
              { value: 3, text: 'oft' },
              { value: 4, text: 'immer' }
            ]
          },
          {
            id: 'HOOS_SYM2',
            question: 'Haben Sie Schwierigkeiten, die Beine zu spreizen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_SYM3',
            question: 'Haben Sie Schwierigkeiten, beim Gehen große Schritte zu machen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_SYM4',
            question: 'Wie ausgeprägt ist die Steifigkeit in Ihrem Hüftgelenk wenn Sie morgens aufwachen?',
            options: [
              { value: 0, text: 'gar nicht' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßig' },
              { value: 3, text: 'stark' },
              { value: 4, text: 'sehr stark' }
            ]
          },
          {
            id: 'HOOS_SYM5',
            question: 'Wie ausgeprägt ist die Steifigkeit in Ihrem Hüftgelenk nach dem Sitzen, Liegen oder Ausruhen im Verlauf des Tages?',
            options: [
              { value: 0, text: 'niemals' },
              { value: 1, text: 'selten' },
              { value: 2, text: 'manchmal' },
              { value: 3, text: 'oft' },
              { value: 4, text: 'immer' }
            ]
          }
        ]
      },
      {
        id: 'pain',
        title: 'Subskala Schmerz',
        instruction: 'Wie starke Schmerzen verspürten Sie in der Hüfte in der letzten Woche während der folgenden Tätigkeiten:',
        questions: [
          {
            id: 'HOOS_PAIN1',
            question: 'Wie oft haben Sie Schmerzen in der Hüfte?',
            options: [
              { value: 0, text: 'nie' },
              { value: 1, text: 'monatlich' },
              { value: 2, text: 'wöchentlich' },
              { value: 3, text: 'täglich' },
              { value: 4, text: 'immer' }
            ]
          },
          {
            id: 'HOOS_PAIN2',
            question: 'Vollständiges Strecken der Hüfte (Aufrichten)?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN3',
            question: 'Maximales Beugen der Hüfte?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN4',
            question: 'Gehen in unebenem Gelände?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN5',
            question: 'Treppen hinauf- oder hinuntersteigen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN6',
            question: 'Nachts im Bett?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN7',
            question: 'Sitzen oder Liegen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN8',
            question: 'Aufrecht stehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          },
          {
            id: 'HOOS_PAIN9',
            question: 'Gehen auf hartem Boden?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'starke' },
              { value: 4, text: 'sehr starke' }
            ]
          }
        ]
      },
      {
        id: 'adl',
        title: 'Subskala Aktivitäten des alltäglichen Lebens',
        instruction: 'Die nachfolgenden Fragen beziehen sich auf Ihre körperliche Funktion, das heißt, Ihre Fähigkeit, sich zu bewegen und für sich zu sorgen. Geben Sie bitte für jede der folgenden Tätigkeiten an, wie große Schwierigkeiten Ihnen Ihre Hüfte während der letzten Woche bereitet hat.',
        questions: [
          {
            id: 'HOOS_ADL1',
            question: 'Treppen hinuntersteigen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL2',
            question: 'Treppen hinaufsteigen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL3',
            question: 'Vom Sitzen aufstehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL4',
            question: 'Stehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL5',
            question: 'Sich bücken/einen Gegenstand vom Boden aufheben?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL6',
            question: 'Gehen in unebenem Gelände?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL7',
            question: 'Einsteigen in ein Auto/Aussteigen aus einem Auto?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL8',
            question: 'Einkaufen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL9',
            question: 'Socken/Strümpfe anziehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL10',
            question: 'Vom Bett aufstehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL11',
            question: 'Socken/Strümpfe ausziehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL12',
            question: 'Im Bett liegen (Drehen mit Beibehalten der Hüftposition)?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'einige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL13',
            question: 'Einsteigen in die Badewanne/Aussteigen aus der Badewanne?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL14',
            question: 'Sitzen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL15',
            question: 'Sich auf die Toilette hinsetzen und wieder aufstehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL16',
            question: 'Schwere Hausarbeit verrichten (Kisten tragen, Boden wischen…)?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_ADL17',
            question: 'Leichte Hausarbeit verrichten (Kochen, Staub wischen…)?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          }
        ]
      },
      {
        id: 'sport',
        title: 'Subskala sportliche Aktivitäten/Freizeit',
        instruction: 'Die folgenden Aktivitäten beziehen sich auf Ihre körperliche Funktion bei anspruchsvolleren Tätigkeiten. Geben Sie bitte für jede der folgenden Tätigkeiten an, wie große Schwierigkeiten Ihnen Ihre Hüfte während der letzten Woche bereitet hat.',
        questions: [
          {
            id: 'HOOS_SPORT1',
            question: 'Kauern/in die Hocke gehen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_SPORT2',
            question: 'Rennen?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_SPORT3',
            question: 'Drehen auf dem belasteten Bein?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          },
          {
            id: 'HOOS_SPORT4',
            question: 'Gehen in unebenem Gelände?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          }
        ]
      },
      {
        id: 'qol',
        title: 'Subskala Lebensqualität',
        instruction: '',
        questions: [
          {
            id: 'HOOS_QOL1',
            question: 'Wie oft sind Sie sich Ihres Hüftproblems bewusst?',
            options: [
              { value: 0, text: 'nie' },
              { value: 1, text: 'monatlich' },
              { value: 2, text: 'wöchentlich' },
              { value: 3, text: 'täglich' },
              { value: 4, text: 'immer' }
            ]
          },
          {
            id: 'HOOS_QOL2',
            question: 'Haben Sie Ihren Lebensstil verändert, um Tätigkeiten zu vermeiden, die Ihrer Hüfte schaden könnten?',
            options: [
              { value: 0, text: 'gar nicht' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßig' },
              { value: 3, text: 'stark' },
              { value: 4, text: 'sehr stark' }
            ]
          },
          {
            id: 'HOOS_QOL3',
            question: 'Wie sehr belastet Sie Ihr fehlendes Vertrauen in Ihre Hüfte?',
            options: [
              { value: 0, text: 'gar nicht' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßig' },
              { value: 3, text: 'stark' },
              { value: 4, text: 'sehr stark' }
            ]
          },
          {
            id: 'HOOS_QOL4',
            question: 'Wie groß sind die Schwierigkeiten, die Sie im Allgemeinen mit Ihrer Hüfte haben?',
            options: [
              { value: 0, text: 'keine' },
              { value: 1, text: 'wenig' },
              { value: 2, text: 'mäßige' },
              { value: 3, text: 'große' },
              { value: 4, text: 'sehr große' }
            ]
          }
        ]
      }
    ],
    en: [
      // Version anglaise à implémenter si nécessaire
    ]
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => {
      const existingResponse = prev[questionId];
      
      // Si la réponse existe et a la même valeur, garder l'ancien timestamp
      if (existingResponse && existingResponse.value === value) {
        return prev;
      }
      
      // Si c'est une nouvelle réponse ou une modification, utiliser le nouveau timestamp local
      return {
        ...prev,
        [questionId]: { 
          value, 
          timestamp: createLocalTimestamp()
        }
      };
    });
  };

  const calculateCompletionRate = () => {
    const allQuestions = questions[language].flatMap(section => 
      section.questions.map(q => q.id)
    );
    
    if (allQuestions.length === 0) return 0;
    
    const answeredQuestions = allQuestions.filter(questionId => 
      responses[questionId] && responses[questionId].value !== undefined
    );
    
    return answeredQuestions.length / allQuestions.length;
  };

  const isComplete = () => {
    return calculateCompletionRate() === 1;
  };

  const handleSubmit = async () => {
    if (!isComplete()) return;

    setLoading(true);
    setError(null);

    try {
      // Calculer un score basé sur les réponses (pour l'instant, on peut utiliser le taux de completion)
      const score = Math.round(calculateCompletionRate() * 100);
      
      const responseData = {
        patientId: patientData.patientId,
        taskId: taskId,
        questionnaireType: 'HOOS',
        responses: JSON.stringify(responses),
        score: score,
        language: language,
        submissionDate: createLocalTimestamp()
      };

      console.log('Soumission HOOS avec données:', responseData);
      
      const response = await apiService.submitQuestionnaire(responseData);

      if (response.success) {
        onComplete && onComplete(responses);
      } else {
        setError(response.message || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur soumission questionnaire:', error);
      setError('Erreur lors de la soumission du questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestions = questions[language] || questions.de;

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div className="questionnaire-header">
          <div className="questionnaire-title">HOOS - Hip disability and Osteoarthritis Outcome Score</div>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="questionnaire-body">
          <div className="questionnaire-instructions">
            <p><strong>Einleitung</strong></p>
            <p>Der folgende Fragebogen dient der Erfassung von Beschwerden und Problemen, die durch Ihr Hüftgelenk verursacht werden. Die dadurch gewonnenen Informationen werden uns helfen einzuschätzen, wie es Ihnen mit Ihrer Hüfte geht und wie gut Sie in der Lage sind, Ihre üblichen Aktivitäten zu verrichten.</p>
            <p>Beantworten Sie bitte jede Frage durch Ankreuzen des zugehörigen Kästchens. Bitte nur ein Kästchen pro Frage ankreuzen. Wenn Sie sich unsicher sind, wie Sie die Frage beantworten sollen, wählen Sie die Antwort aus, die Ihnen am zutreffendsten erscheint.</p>
            <p>Bitte beantworten Sie alle Fragen gemäß Ihrem aktuellen Zustand. Sollten Sie momentan keine Beschwerden haben, dann bewerten Sie die Fragen entsprechend Ihrem Zustand in der vergangenen Woche.</p>
          </div>
          
          {currentQuestions.map((section, index) => (
            <div key={section.id} className="questions-list" ref={el => sectionRefs.current[index] = el}>
              <h3 className={`section-questionnaire ${activeSection === index ? 'active' : ''}`}>
                {section.title}
              </h3>
              {section.instruction && (
                <p className="section-instruction">{section.instruction}</p>
              )}
              
              {section.questions.map((question) => (
                <div key={question.id} className="question-item">
                  <h4 className="question-title">{question.question}</h4>
                  
                  <div className="question-options">
                    {question.options.map((option) => (
                      <label key={option.value} className="option-label">
                        <input
                          type="radio"
                          name={question.id}
                          value={option.value}
                          checked={responses[question.id] && responses[question.id].value === option.value}
                          onChange={(e) => handleResponseChange(question.id, parseInt(e.target.value))}
                          className="option-input"
                        />
                        <span className="option-text">{option.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Barre de progression des sections */}
        <div className="questionnaire-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${calculateCompletionRate() * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="questionnaire-footer">
          <button
            className={`submit-btn ${!isComplete() || loading ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isComplete() || loading}
          >
            {loading 
              ? (language === 'de' ? 'Wird gesendet...' : 'Submitting...')
              : (language === 'de' ? 'Absenden' : 'Submit')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default HOOSQuestionnaire; 